// src/components/ChatBot/ChatContext.js
import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { getToken } from "../../rtkq/services/localStorageService";

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_SERVER_URL || "http://127.0.0.1:8000";

const ChatContext = createContext();

export const useChat = () => useContext(ChatContext);

export const ChatProvider = ({ children }) => {
  console.log("ChatProvider initializing/re-rendering");
  
  // Existing states
  const [messages, setMessages] = useState([]);
  const [overallRating, setOverallRating] = useState(0);
  const [mode, setMode] = useState('conversation');
  const [subType, setSubType] = useState('');
  const [remainingRequests, setRemainingRequests] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [contentInfo, setContentInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentModeSubType, setCurrentModeSubType] = useState(null);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);

  // Stream states
  const [activeStream, setActiveStream] = useState('chat'); // 'chat' or 'tutor'
  const [tutorMessages, setTutorMessages] = useState([]); // State for tutor messages

  // Refs
  const currentAudioRef = useRef(null);
  const audioQueueRef = useRef([]);
  const incompleteChunkRef = useRef('');
  const chatContainerRef = useRef(null);
  const lastTutorUpdateRef = useRef(Date.now());

  // Subtype options state
  const [subtypeOptions, setSubtypeOptions] = useState({
    conversation: [], passage: [], inquiry: [],
  });

  // Fetch available subtypes from the backend
  const fetchSubtypeOptions = async (modeType) => {
    try {
      const { access_token } = getToken();
      if (!access_token) { 
        console.error('No access token'); 
        return null; 
      }
      
      const response = await fetch(`${BACKEND_BASE_URL}/api/chat/content/available_subtypes/?mode=${modeType}`, {
        headers: { 'Authorization': `Bearer ${access_token}` }
      });
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const subtypeData = await response.json();
      
      if (subtypeData.length === 0) console.warn(`No content for mode ${modeType}`);
      
      setSubtypeOptions(prev => ({ ...prev, [modeType]: subtypeData }));
      
      return subtypeData.length > 0 ? subtypeData[0].value : null;
    } catch (error) { 
      console.error(`Error fetching subtypes for ${modeType}:`, error); 
      return null; 
    }
  };

  // Load initialization data and history
  const loadInitAndHistory = async (modeType, subTypeValue) => {
    setIsLoading(true);
    setMessages([]);
    setTutorMessages([]); // Clear previous messages
    
    try {
        const { access_token } = getToken();
        if (!access_token) throw new Error('No access token found');

        // 1. Get Init data (for main chat context and initial message)
        const initResponse = await fetch(`${BACKEND_BASE_URL}/api/chat/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${access_token}` },
            body: JSON.stringify({ init: true, mode: modeType, subType: subTypeValue }),
        });
        
        if (!initResponse.ok) throw new Error(`Init HTTP error! status: ${initResponse.status}`);
        
        const initData = await initResponse.json();
        setContentInfo(initData.content_info);
        
        if (initData.daily_requests_remaining !== undefined) {
            setRemainingRequests(initData.daily_requests_remaining);
        }

        // 2. Get ALL History (Chat and Tutor) from the updated endpoint
        const historyResponse = await fetch(`${BACKEND_BASE_URL}/api/chat/history/?mode=${modeType}&subtype=${subTypeValue}`, {
            headers: { 'Authorization': `Bearer ${access_token}` }
        });
        
        if (!historyResponse.ok) throw new Error(`History HTTP error! status: ${historyResponse.status}`);
        
        const historyData = await historyResponse.json(); // Expects { chatMessages: [], tutorMessages: [], overall_rating: X }

        // 3. Populate State Arrays
        const initialChatMessage = initData.content_info?.initial_message || "Hello! Let's start conversing!";
        
        console.log("Setting initial chat messages:", [{ role: 'bot', content: initialChatMessage }, ...(historyData.chatMessages || [])]);
        setMessages([{ role: 'bot', content: initialChatMessage }, ...(historyData.chatMessages || [])]);
        
        // Populate tutorMessages from historyData
        const loadedTutorMessages = historyData.tutorMessages || [];
        if (loadedTutorMessages.length === 0) {
            // If no tutor history, add the static initial message
            console.log("Setting initial tutor message");
            setTutorMessages([{
                role: 'bot',
                content: "Hello! I'm your English Tutor. How can I help you improve based on your conversation?"
            }]);
        } else {
            console.log("Setting loaded tutor messages:", loadedTutorMessages);
            setTutorMessages(loadedTutorMessages);
        }

        setOverallRating(historyData.overall_rating || 0);
        return true;
    } catch (error) {
        console.error('Error loading init and history:', error);
        setMessages([{ role: 'bot', content: `Error loading chat: ${error.message}` }]);
        setTutorMessages([{ role: 'bot', content: "Tutor is currently unavailable due to an error."}]);
        return false;
    } finally {
        setIsLoading(false);
    }
  };

  // Audio handling functions
  const playNextInQueue = () => {
    if (audioQueueRef.current.length === 0) {
      setIsPlaying(false);
      return;
    }

    const nextAudioBase64 = audioQueueRef.current.shift();
    console.log("Playing next audio in queue...");
    const audio = new Audio(`data:audio/mp3;base64,${nextAudioBase64}`);
    currentAudioRef.current = audio;

    audio.onended = () => {
      console.log("Audio ended, moving to next in queue");
      currentAudioRef.current = null;
      playNextInQueue();
    };

    audio.onerror = (error) => {
      console.error('Error playing audio:', error);
      currentAudioRef.current = null;
      playNextInQueue();
    };

    audio.play().catch(error => {
      console.error('Error starting audio playback:', error);
      currentAudioRef.current = null;
      playNextInQueue();
    });
  };

  const playAudio = (audioBase64) => {
    console.log("Adding audio to queue, base64 length:", audioBase64.length);
    audioQueueRef.current.push(audioBase64);
    if (!currentAudioRef.current) {
      console.log("Starting audio playback queue");
      setIsPlaying(true);
      playNextInQueue();
    }
  };

  // Mode change handler
  const handleModeChange = async (newMode) => {
    if (newMode === mode) return;
    
    setIsLoading(true);
    try {
      const defaultSubTypeValue = await fetchSubtypeOptions(newMode);
      if (!defaultSubTypeValue) {
        console.error(`No available content for mode: ${newMode}`);
        setMessages([{ role: 'bot', content: `No content available for ${newMode} mode.` }]);
        setTutorMessages([{ role: 'bot', content: "Please select content for the main chat first." }]);
        setMode(newMode); // Still update mode so UI reflects change
        setSubType(''); // Clear subtype
        setContentInfo(null);
        return;
      }
      
      await loadInitAndHistory(newMode, defaultSubTypeValue);
      setMode(newMode);
      setSubType(defaultSubTypeValue);
      setCurrentModeSubType({ mode: newMode, subType: defaultSubTypeValue });
      setActiveStream('chat'); // Reset to chat stream on mode/topic change
    } catch (error) {
      console.error('Error in handleModeChange:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // // Process JSON chunks from streaming responses
  // const processJsonChunk = (jsonStr) => {
  //   try {
  //     const data = JSON.parse(jsonStr);
  //     const targetStream = data.stream || 'chat'; // Default to 'chat' if backend doesn't send it

  //     console.log("Processing chunk with stream type:", targetStream, "Type:", data.type);

  //     if (data.type === 'rating') {
  //       console.log(`Rating received (Stream: ${targetStream}):`, data);
  //       if (data.overall_rating !== undefined) {
  //         setOverallRating(data.overall_rating);
  //       }
  //       // IMPORTANT: Ratings only apply to the main 'chat' stream's messages
  //       if (targetStream === 'chat' && data.message_rating !== undefined) {
  //         setMessages(prevChatMessages => { // Ensure we are updating the 'chat' messages
  //           const lastUserMessageIndex = [...prevChatMessages].reverse().findIndex(msg => msg.role === 'user');
  //           if (lastUserMessageIndex === -1) return prevChatMessages;
  //           const actualIndex = prevChatMessages.length - 1 - lastUserMessageIndex;
  //           const lastUserMsg = prevChatMessages[actualIndex];
  //           return [
  //             ...prevChatMessages.slice(0, actualIndex),
  //             { ...lastUserMsg, rating: data.message_rating, correctedSentence: data.corrected_sentence || null },
  //             ...prevChatMessages.slice(actualIndex + 1)
  //           ];
  //         });
  //       }
  //     } else if (data.type === 'transcription_only') {
  //       console.log(`Transcription received (Stream: ${targetStream}):`, data);
  //       // Add the user's transcribed message to the correct stream
  //       if (targetStream === 'tutor') {
  //         setTutorMessages(prev => {
  //           console.log("Adding user transcription to tutor messages");
  //           return [...prev, { role: 'user', content: data.transcription, rating: null }];
  //         });
  //       } else {
  //         setMessages(prev => {
  //           console.log("Adding user transcription to chat messages");
  //           return [...prev, { role: 'user', content: data.transcription, rating: null }];
  //         });
  //       }
  //     } else if (data.type === 'audio_segment') {
  //       console.log(`Audio segment received (Stream: ${targetStream}):`, data.text);
  //       const textContent = data.text || '';
  //       if (textContent) {
  //         // Force re-render by creating entirely new array
  //         if (targetStream === 'tutor') {
  //           setTutorMessages(prev => {
  //             console.log("Tutor messages before update:", prev);
  //             const lastMessage = prev[prev.length - 1];
  //             let newMessages;
              
  //             if (lastMessage && lastMessage.role === 'bot') {
  //               // Append to existing bot message
  //               const updatedMessage = { 
  //                 ...lastMessage, 
  //                 content: lastMessage.content + ' ' + textContent 
  //               };
  //               newMessages = [...prev.slice(0, -1), updatedMessage];
  //             } else {
  //               // Add new bot message
  //               newMessages = [...prev, { role: 'bot', content: textContent }];
  //             }
              
  //             console.log("Tutor messages after update:", newMessages);
  //             return newMessages;
  //           });
  //         } else {
  //           setMessages(prev => {
  //             console.log("Chat messages before update:", prev);
  //             const lastMessage = prev[prev.length - 1];
  //             let newMessages;
              
  //             if (lastMessage && lastMessage.role === 'bot') {
  //               // Append to existing bot message
  //               const updatedMessage = { 
  //                 ...lastMessage, 
  //                 content: lastMessage.content + ' ' + textContent 
  //               };
  //               newMessages = [...prev.slice(0, -1), updatedMessage];
  //             } else {
  //               // Add new bot message
  //               newMessages = [...prev, { role: 'bot', content: textContent }];
  //             }
              
  //             console.log("Chat messages after update:", newMessages);
  //             return newMessages;
  //           });
  //         }
  //       }

        
  //       // Play audio ONLY if it's for the 'chat' stream
  //       if (data.audio && targetStream === 'chat') {
  //         console.log("Found audio data, length:", data.audio.length);
  //         playAudio(data.audio);
  //       }
  //     } else if (data.type === 'complete_response') {
  //       console.log(`Complete response signal (Stream: ${targetStream}). Full Text:`, data.response);
  //       // If this is for the Tutor stream, and it's a non-streaming response,
  //       // we might need to add it here if not handled by segments.
  //       if (targetStream === 'tutor' && data.response) {
  //         console.log("Got complete tutor response");
          
  //         // For tutor stream, always replace last bot message or add new one
  //         setTutorMessages(prev => {
  //           const lastMessage = prev[prev.length - 1];
            
  //           // Always create a completely new array for React to detect the change
  //           let newMessages;
            
  //           if (lastMessage && lastMessage.role === 'user') {
  //             // Add new bot message after user message
  //             newMessages = [...prev, { role: 'bot', content: data.response }];
  //           } else if (lastMessage && lastMessage.role === 'bot') {
  //             // Replace last bot message with updated content
  //             newMessages = [...prev.slice(0, -1), { role: 'bot', content: data.response }];
  //           } else {
  //             // Add new bot message if no messages exist
  //             newMessages = [...prev, { role: 'bot', content: data.response }];
  //           }
            
  //           console.log("Updated tutor messages:", newMessages);
            
  //           // Record the update time
  //           lastTutorUpdateRef.current = Date.now();
            
  //           // Force a render cycle
  //           setTimeout(() => {
  //             console.log("Forcing re-render of tutor messages");
  //             setTutorMessages([...newMessages]);
  //           }, 50);
            
  //           return newMessages;
  //         });
  //       } else if (targetStream === 'chat') {
  //         // For 'chat' stream, update the final message if needed
  //         setMessages(prev => {
  //           console.log("Chat messages before complete response update:", prev);
  //           const lastMessage = prev[prev.length - 1];
            
  //           // Only update if we have a response and it's different from the last message
  //           if (data.response && lastMessage && lastMessage.role === 'bot' && lastMessage.content !== data.response) {
  //             return [...prev.slice(0, -1), { ...lastMessage, content: data.response }];
  //           }
  //           return prev;
  //         });
  //       }
  //     }

  //     if (data.daily_requests_remaining !== undefined) {
  //       setRemainingRequests(data.daily_requests_remaining);
  //     }
  //   } catch (e) {
  //     console.error('Error parsing JSON in stream:', e, jsonStr);
  //   }
  // };


  // Process JSON chunks from streaming responses
  const processJsonChunk = (jsonStr) => {
    try {
      console.log("Raw JSON received:", jsonStr);
      const data = JSON.parse(jsonStr);
      const targetStream = data.stream || 'chat'; // Default to 'chat' if backend doesn't send it

      console.log("Processing chunk with stream type:", targetStream, "Type:", data.type);

      if (data.type === 'rating') {
        console.log(`Rating received (Stream: ${targetStream}):`, data);
        if (data.overall_rating !== undefined) {
          setOverallRating(data.overall_rating);
        }
        // IMPORTANT: Ratings only apply to the main 'chat' stream's messages
        if (targetStream === 'chat' && data.message_rating !== undefined) {
          setMessages(prevChatMessages => { // Ensure we are updating the 'chat' messages
            const lastUserMessageIndex = [...prevChatMessages].reverse().findIndex(msg => msg.role === 'user');
            if (lastUserMessageIndex === -1) return prevChatMessages;
            const actualIndex = prevChatMessages.length - 1 - lastUserMessageIndex;
            const lastUserMsg = prevChatMessages[actualIndex];
            return [
              ...prevChatMessages.slice(0, actualIndex),
              { ...lastUserMsg, rating: data.message_rating, correctedSentence: data.corrected_sentence || null },
              ...prevChatMessages.slice(actualIndex + 1)
            ];
          });
        }
      } else if (data.type === 'transcription_only') {
        console.log(`Transcription received (Stream: ${targetStream}):`, data);
        // Add the user's transcribed message to the correct stream
        if (targetStream === 'tutor') {
          setTutorMessages(prev => {
            console.log("Adding user transcription to tutor messages");
            return [...prev, { role: 'user', content: data.transcription, rating: null }];
          });
        } else {
          setMessages(prev => {
            console.log("Adding user transcription to chat messages");
            return [...prev, { role: 'user', content: data.transcription, rating: null }];
          });
        }
      } else if (data.type === 'audio_segment') {
        console.log(`Audio segment received (Stream: ${targetStream}):`, data.text);
        const textContent = data.text || '';
        if (textContent) {
          // Force re-render by creating entirely new array
          if (targetStream === 'tutor') {
            setTutorMessages(prev => {
              console.log("Tutor messages before update:", prev);
              const lastMessage = prev[prev.length - 1];
              let newMessages;
              
              if (lastMessage && lastMessage.role === 'bot') {
                // Append to existing bot message
                const updatedMessage = { 
                  ...lastMessage, 
                  content: lastMessage.content + ' ' + textContent 
                };
                newMessages = [...prev.slice(0, -1), updatedMessage];
              } else {
                // Add new bot message
                newMessages = [...prev, { role: 'bot', content: textContent }];
              }
              
              console.log("Tutor messages after update:", newMessages);
              return newMessages;
            });
          } else {
            setMessages(prev => {
              console.log("Chat messages before update:", prev);
              const lastMessage = prev[prev.length - 1];
              let newMessages;
              
              if (lastMessage && lastMessage.role === 'bot') {
                // Append to existing bot message
                const updatedMessage = { 
                  ...lastMessage, 
                  content: lastMessage.content + ' ' + textContent 
                };
                newMessages = [...prev.slice(0, -1), updatedMessage];
              } else {
                // Add new bot message
                newMessages = [...prev, { role: 'bot', content: textContent }];
              }
              
              console.log("Chat messages after update:", newMessages);
              return newMessages;
            });
          }
        }
        
        // Play audio ONLY if it's for the 'chat' stream
        if (data.audio && targetStream === 'chat') {
          console.log("Found audio data, length:", data.audio.length);
          playAudio(data.audio);
        }
      } else if (data.type === 'complete_response') {
        console.log(`Complete response signal (Stream: ${targetStream}). Full Text:`, data.response);
        // If this is for the Tutor stream, and it's a non-streaming response,
        // we might need to add it here if not handled by segments.
        if (targetStream === 'tutor' && data.response) {
          console.log("Got complete tutor response");
          
          // For tutor stream, always replace last bot message or add new one
          setTutorMessages(prev => {
            const lastMessage = prev[prev.length - 1];
            
            // Always create a completely new array for React to detect the change
            let newMessages;
            
            if (lastMessage && lastMessage.role === 'user') {
              // Add new bot message after user message
              newMessages = [...prev, { role: 'bot', content: data.response }];
            } else if (lastMessage && lastMessage.role === 'bot') {
              // Replace last bot message with updated content
              newMessages = [...prev.slice(0, -1), { role: 'bot', content: data.response }];
            } else {
              // Add new bot message if no messages exist
              newMessages = [...prev, { role: 'bot', content: data.response }];
            }
            
            console.log("Updated tutor messages:", newMessages);
            
            // Record the update time
            lastTutorUpdateRef.current = Date.now();
            
            // Force a render cycle
            setTimeout(() => {
              console.log("Forcing re-render of tutor messages");
              setTutorMessages([...newMessages]);
            }, 50);
            
            return newMessages;
          });
        } else if (targetStream === 'chat') {
          // For 'chat' stream, update the final message if needed
          setMessages(prev => {
            console.log("Chat messages before complete response update:", prev);
            const lastMessage = prev[prev.length - 1];
            
            // Only update if we have a response and it's different from the last message
            if (data.response && lastMessage && lastMessage.role === 'bot' && lastMessage.content !== data.response) {
              return [...prev.slice(0, -1), { ...lastMessage, content: data.response }];
            }
            return prev;
          });
        }
      } else if (data.type === 'tutor_voice_response') {
        console.log(`Tutor voice response received:`, data);
        
        // First handle the transcription if present
        if (data.transcription) {
          setTutorMessages(prev => {
            console.log("Adding user transcription to tutor messages");
            return [...prev, { role: 'user', content: data.transcription, rating: null }];
          });
        }
        
        // Then handle the response JSON
        try {
          // The response might be a string (JSON) or an object
          let responseObj = data.response;
          
          // If it's a string and looks like JSON, parse it
          if (typeof responseObj === 'string' && responseObj.startsWith('{')) {
            responseObj = JSON.parse(responseObj);
          }
          
          // Extract the actual response content
          const responseText = responseObj.response || responseObj;
          
          // Update the tutor messages with the bot response
          setTutorMessages(prev => {
            console.log("Adding bot response to tutor messages:", responseText);
            
            // Create a completely new array for React to detect the change
            const newMessages = [...prev, { role: 'bot', content: responseText }];
            
            // Force a re-render after a small delay
            setTimeout(() => {
              console.log("Forcing re-render of tutor messages");
              setTutorMessages([...newMessages]);
            }, 100);
            
            return newMessages;
          });
        } catch (err) {
          console.error("Error processing tutor response:", err, data.response);
          
          // If there was an error parsing, try to use the response directly as a string
          if (data.response) {
            setTutorMessages(prev => {
              const responseStr = typeof data.response === 'string' 
                ? data.response 
                : JSON.stringify(data.response);
                
              return [...prev, { role: 'bot', content: responseStr }];
            });
          }
        }
        
        // Record the update time for re-render logic
        lastTutorUpdateRef.current = Date.now();
      }

      if (data.daily_requests_remaining !== undefined) {
        setRemainingRequests(data.daily_requests_remaining);
      }
    } catch (e) {
      console.error('Error parsing JSON in stream:', e, jsonStr);
    }
  };


  // Handle streaming responses from the server
  const handleStreamingResponse = async (reader) => {
    const decoder = new TextDecoder();
    
    try {
      console.log("Starting to process streaming response");
      let count = 0;
      
      while (true) {
        const { value, done } = await reader.read();
        
        if (done) {
          console.log("Stream complete");
          break;
        }
        
        count++;
        const chunk = incompleteChunkRef.current + decoder.decode(value, { stream: true });
        console.log(`Processing chunk #${count}, size: ${chunk.length}`);
        
        let lastNewlineIndex = chunk.lastIndexOf('\n');
        
        if (lastNewlineIndex === -1) {
          incompleteChunkRef.current = chunk;
          console.log("No complete JSON objects in this chunk, continuing...");
          continue;
        }
        
        const completeChunk = chunk.substring(0, lastNewlineIndex);
        incompleteChunkRef.current = chunk.substring(lastNewlineIndex + 1);
        
        const jsonChunks = completeChunk.split('\n');
        console.log(`Found ${jsonChunks.length} JSON objects in chunk`);
        
        for (const jsonStr of jsonChunks) {
          if (jsonStr.trim()) {
            console.log("Processing JSON:", jsonStr.substring(0, 100) + "...");
            processJsonChunk(jsonStr);
            
            // Force immediate UI update after each chunk
            await new Promise(resolve => setTimeout(resolve, 10));
          }
        }
      }
      
      if (incompleteChunkRef.current.trim()) {
        console.log("Processing final chunk:", incompleteChunkRef.current.substring(0, 100) + "...");
        processJsonChunk(incompleteChunkRef.current);
        incompleteChunkRef.current = '';
      }
      
      console.log("Stream processing complete");
      
    } catch (error) {
      console.error('Error reading stream:', error);
    }
  };

  // Initial load effect - fetch available content for all modes
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        const modesArray = ['conversation', 'passage', 'inquiry'];
        const initialMode = 'conversation';
        const defaultSubTypeValue = await fetchSubtypeOptions(initialMode); // Fetch options and get default
        if (defaultSubTypeValue) {
          await loadInitAndHistory(initialMode, defaultSubTypeValue);
          setSubType(defaultSubTypeValue); // Set the default subtype
          setCurrentModeSubType({ mode: initialMode, subType: defaultSubTypeValue });
        } else {
          console.error(`No available content for initial mode: ${initialMode}`);
          setMessages([{role: 'bot', content: `No content available for ${initialMode} mode.`}]);
        }
        for (const modeType of modesArray) { // Pre-fetch options for other modes
            if (modeType !== initialMode) await fetchSubtypeOptions(modeType);
        }
      } catch (error) { 
        console.error('Error in initial data load:', error);
      } finally { 
        setIsLoading(false); 
      }
    };
    loadInitialData();
  }, []);

  // Effect for handling subtype changes
  useEffect(() => {
    const handleSubTypeChange = async () => {
      if (!mode || !subType) return;
      const isChange = !currentModeSubType || subType !== currentModeSubType.subType || mode !== currentModeSubType.mode;
      if (isChange) {
        setIsLoading(true);
        try {
          await loadInitAndHistory(mode, subType);
          setCurrentModeSubType({ mode, subType });
          setActiveStream('chat'); // Always reset to chat when topic/mode changes
        } catch (error) { 
          console.error('Error in subtype change:', error);
        } finally { 
          setIsLoading(false); 
        }
      }
    };
    if (subType) handleSubTypeChange(); // Only run if subType is set
  }, [subType, mode]); // Dependency on mode added for safety

  // Monitor message state changes
  useEffect(() => {
    console.log("Messages state updated, new count:", messages.length);
  }, [messages]);

  // Monitor tutorMessages state changes and force re-render if needed
  useEffect(() => {
    console.log("TutorMessages state updated, new count:", tutorMessages.length);
    
    // This effect specifically helps with tutor messages rendering
    if (tutorMessages.length > 0) {
      console.log("Tutor messages updated, forcing re-render if needed");
      // Use a timeout to ensure the state has settled
      const timeSinceLastUpdate = Date.now() - lastTutorUpdateRef.current;
      if (timeSinceLastUpdate < 500) {
        setTimeout(() => {
          // The empty setState is a trick to force a re-render if needed
          setTutorMessages(current => [...current]);
        }, 100);
      }
    }
  }, [tutorMessages]);

  // Check if content is completed by counting user messages
  const isContentCompleted = (mode, subtype) => {
    // Filter messages for this specific content
    const userMessageCount = messages.filter(msg => msg.role === 'user').length;
    
    // If we have 10 or more user messages, consider it completed
    return userMessageCount >= 10;
  };

  const value = {
    messages, setMessages,
    tutorMessages, setTutorMessages,
    mode, setMode: handleModeChange,
    subType, setSubType,
    activeStream, setActiveStream,
    remainingRequests, subtypeOptions, contentInfo, chatContainerRef,
    handleStreamingResponse, incompleteChunkRef, isLoading, overallRating,
    setOverallRating, isWaitingForResponse, setIsWaitingForResponse,
    isContentCompleted,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export default ChatProvider;
